import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';

/* ─────────────────────────── Reference data ─────────────────────────── */

const STORAGE_KEY = 'kro_payment_workflow_v1';

// Payments at or above this amount route to an approver before scheduling.
const APPROVAL_THRESHOLD = 10000;

const STATUS_META = {
  DRAFT:            { label: 'Draft',            badge: 'secondary', icon: 'bi-pencil-square' },
  PENDING_APPROVAL: { label: 'Pending Approval', badge: 'warning',   icon: 'bi-hourglass-split' },
  APPROVED:         { label: 'Approved',         badge: 'primary',   icon: 'bi-check2-circle' },
  SCHEDULED:        { label: 'Scheduled',        badge: 'info',      icon: 'bi-calendar-check' },
  PROCESSING:       { label: 'Processing',       badge: 'info',      icon: 'bi-arrow-repeat' },
  SETTLED:          { label: 'Settled',          badge: 'success',   icon: 'bi-patch-check-fill' },
  EXCEPTION:        { label: 'Exception',        badge: 'danger',    icon: 'bi-exclamation-triangle-fill' },
  REJECTED:         { label: 'Rejected',         badge: 'dark',      icon: 'bi-x-circle' },
};

// Ordered happy path — drives the progress bar on each payment row.
const PIPELINE = ['DRAFT', 'PENDING_APPROVAL', 'APPROVED', 'SCHEDULED', 'PROCESSING', 'SETTLED'];

const PAYMENT_METHODS = [
  { id: 'ACH',   label: 'ACH Bank Transfer', speed: '1–3 business days', fee: 'No fee',  icon: 'bi-bank' },
  { id: 'WIRE',  label: 'Wire (Fedwire)',    speed: 'Same day',          fee: '$15.00',  icon: 'bi-lightning-charge-fill' },
  { id: 'CHECK', label: 'Paper Check',       speed: '5–7 business days', fee: '$1.75',   icon: 'bi-envelope-paper' },
  { id: 'VCARD', label: 'Virtual Card',      speed: 'Immediate',         fee: 'No fee',  icon: 'bi-credit-card-2-front' },
];

const FUNDING_ACCOUNTS = [
  { id: 'MERCURY_OPS',  label: 'Mercury — Operating',        masked: '••••4821', balance: 4820150.00 },
  { id: 'COLUMN_SETTL', label: 'Column N.A. — Settlement',   masked: '••••7734', balance: 12930400.00 },
];

const PAYEES = [
  { id: 'P-1687',  name: '1687 Productions',              network: 'Distribution Channel',   method: 'ACH',   terms: 'Net 30', verified: true },
  { id: 'P-AIJSF', name: 'AI for Job Security Foundation', network: 'Distribution Channel',   method: 'ACH',   terms: 'Net 15', verified: true },
  { id: 'P-SOFR',  name: 'So Fraiche',                    network: 'Supplier Network',       method: 'ACH',   terms: 'Net 30', verified: true },
  { id: 'P-ASN',   name: 'African Supplier Network',       network: 'Supplier Network',       method: 'WIRE',  terms: 'Net 45', verified: true },
  { id: 'P-SEXI',  name: 'Sexi Cycle',                    network: 'Distribution Channel',   method: 'ACH',   terms: 'Net 30', verified: true },
  { id: 'P-BITGO', name: 'BitGo Trust Company',            network: 'Institutional Exchange', method: 'WIRE',  terms: 'Due on receipt', verified: true },
];

const GL_CATEGORIES = [
  '6100 · Distribution & Channel Costs',
  '6200 · Supplier Goods & Services',
  '6300 · Custody & Settlement Fees',
  '6400 · Professional Services',
  '6500 · Technology & Infrastructure',
  '7100 · Community & Impact Programs',
];

const EXCHANGE_NETWORKS = ['Institutional Exchange', 'Distribution Channel', 'Supplier Network'];

const EXCEPTION_CODES = [
  { code: 'INSUFFICIENT_FUNDS',    label: 'Insufficient funds in funding account' },
  { code: 'INVALID_ACCOUNT',       label: 'Payee bank account details invalid' },
  { code: 'DUPLICATE_PAYMENT',     label: 'Possible duplicate of an existing payment' },
  { code: 'LIMIT_EXCEEDED',        label: 'Daily payment limit exceeded for payee' },
  { code: 'PAYEE_UNVERIFIED',      label: 'Payee verification still pending' },
  { code: 'ACH_RETURN_R01',        label: 'ACH return R01 — insufficient funds at receiving bank' },
];

/* ─────────────────────────── Helpers ─────────────────────────── */

const money = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(Number(n) || 0);

const shortMoney = (n) => {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return money(v);
};

const nowIso = () => new Date().toISOString();

const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtDate = (d) => (d ? new Date(d + 'T00:00:00').toLocaleDateString() : '—');

let seq = 0;
const newRef = () => {
  seq += 1;
  const stamp = new Date().toISOString().slice(2, 10).replace(/-/g, '');
  return `PMT-${stamp}-${String(Date.now()).slice(-4)}${seq}`;
};

const methodLabel = (id) => PAYMENT_METHODS.find(m => m.id === id)?.label || id;
const accountLabel = (id) => {
  const a = FUNDING_ACCOUNTS.find(x => x.id === id);
  return a ? `${a.label} ${a.masked}` : id;
};

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

/* ─────────────────────────── Seed records ─────────────────────────── */

const SEED_PAYMENTS = [
  {
    ref: 'PMT-260722-0001', payeeId: 'P-1687', payeeName: '1687 Productions', network: 'Distribution Channel',
    amount: 48500, method: 'ACH', fundingAccount: 'MERCURY_OPS', payDate: '2026-07-22',
    invoiceRef: 'INV-1687-0412', category: '6100 · Distribution & Channel Costs', memo: 'Q3 channel activation — creative delivery',
    internalNote: 'Approved against SOW addendum 3.', status: 'SETTLED',
    createdAt: '2026-07-20T15:12:00Z', createdBy: 'a.clayborne@krotrust.org',
    approvedBy: 'isadmin@krotrust.org', approvedAt: '2026-07-20T18:40:00Z',
    settledAt: '2026-07-22T14:05:00Z', bankRef: 'ACH-TRACE-091000019384756',
    reconciled: true, reconciledAt: '2026-07-23T09:15:00Z',
    history: [
      { at: '2026-07-20T15:12:00Z', actor: 'a.clayborne@krotrust.org', action: 'Payment created', detail: 'Draft opened for 1687 Productions — $48,500.00 via ACH' },
      { at: '2026-07-20T15:26:00Z', actor: 'a.clayborne@krotrust.org', action: 'Submitted for approval', detail: 'Amount above $10,000.00 threshold — routed to approver' },
      { at: '2026-07-20T18:40:00Z', actor: 'isadmin@krotrust.org', action: 'Approved', detail: 'Invoice INV-1687-0412 matched to purchase order' },
      { at: '2026-07-21T08:00:00Z', actor: 'system', action: 'Scheduled', detail: 'Queued for 2026-07-22 settlement window' },
      { at: '2026-07-22T14:05:00Z', actor: 'system', action: 'Settled', detail: 'ACH trace ACH-TRACE-091000019384756 confirmed by Mercury' },
      { at: '2026-07-23T09:15:00Z', actor: 'a.clayborne@krotrust.org', action: 'Reconciled', detail: 'Matched to bank feed — zero variance' },
    ],
  },
  {
    ref: 'PMT-260724-0002', payeeId: 'P-ASN', payeeName: 'African Supplier Network', network: 'Supplier Network',
    amount: 126000, method: 'WIRE', fundingAccount: 'COLUMN_SETTL', payDate: '2026-07-28',
    invoiceRef: 'INV-ASN-2261', category: '6200 · Supplier Goods & Services', memo: 'Regional supplier program — tranche 2',
    internalNote: '', status: 'PENDING_APPROVAL',
    createdAt: '2026-07-24T11:02:00Z', createdBy: 'a.clayborne@krotrust.org',
    reconciled: false,
    history: [
      { at: '2026-07-24T11:02:00Z', actor: 'a.clayborne@krotrust.org', action: 'Payment created', detail: 'Draft opened for African Supplier Network — $126,000.00 via Wire (Fedwire)' },
      { at: '2026-07-24T11:19:00Z', actor: 'a.clayborne@krotrust.org', action: 'Submitted for approval', detail: 'Amount above $10,000.00 threshold — routed to approver' },
    ],
  },
  {
    ref: 'PMT-260725-0003', payeeId: 'P-SOFR', payeeName: 'So Fraiche', network: 'Supplier Network',
    amount: 7250, method: 'ACH', fundingAccount: 'MERCURY_OPS', payDate: '2026-07-27',
    invoiceRef: 'INV-SF-0088', category: '6200 · Supplier Goods & Services', memo: 'July fulfilment run',
    internalNote: 'Below approval threshold — auto-scheduled.', status: 'SCHEDULED',
    createdAt: '2026-07-25T09:31:00Z', createdBy: 'a.clayborne@krotrust.org',
    reconciled: false,
    history: [
      { at: '2026-07-25T09:31:00Z', actor: 'a.clayborne@krotrust.org', action: 'Payment created', detail: 'Draft opened for So Fraiche — $7,250.00 via ACH' },
      { at: '2026-07-25T09:33:00Z', actor: 'system', action: 'Scheduled', detail: 'Below $10,000.00 threshold — no approval required' },
    ],
  },
  {
    ref: 'PMT-260726-0004', payeeId: 'P-SEXI', payeeName: 'Sexi Cycle', network: 'Distribution Channel',
    amount: 15400, method: 'ACH', fundingAccount: 'MERCURY_OPS', payDate: '2026-07-27',
    invoiceRef: 'INV-SX-0173', category: '6100 · Distribution & Channel Costs', memo: 'Retail placement — August cycle',
    internalNote: 'Bank details updated by payee on 07-26 — needs re-verification before release.',
    status: 'EXCEPTION', exceptionCode: 'INVALID_ACCOUNT',
    createdAt: '2026-07-26T13:44:00Z', createdBy: 'a.clayborne@krotrust.org',
    reconciled: false,
    history: [
      { at: '2026-07-26T13:44:00Z', actor: 'a.clayborne@krotrust.org', action: 'Payment created', detail: 'Draft opened for Sexi Cycle — $15,400.00 via ACH' },
      { at: '2026-07-26T13:52:00Z', actor: 'a.clayborne@krotrust.org', action: 'Submitted for approval', detail: 'Amount above $10,000.00 threshold — routed to approver' },
      { at: '2026-07-26T16:10:00Z', actor: 'system', action: 'Exception raised', detail: 'INVALID_ACCOUNT — payee bank account details invalid' },
    ],
  },
  {
    ref: 'PMT-260727-0005', payeeId: 'P-BITGO', payeeName: 'BitGo Trust Company', network: 'Institutional Exchange',
    amount: 32000, method: 'WIRE', fundingAccount: 'COLUMN_SETTL', payDate: '2026-07-29',
    invoiceRef: 'INV-BG-Q3-01', category: '6300 · Custody & Settlement Fees', memo: 'Q3 custody and MPC service fees',
    internalNote: '', status: 'APPROVED',
    createdAt: '2026-07-27T08:15:00Z', createdBy: 'a.clayborne@krotrust.org',
    approvedBy: 'isadmin@krotrust.org', approvedAt: '2026-07-27T10:02:00Z',
    reconciled: false,
    history: [
      { at: '2026-07-27T08:15:00Z', actor: 'a.clayborne@krotrust.org', action: 'Payment created', detail: 'Draft opened for BitGo Trust Company — $32,000.00 via Wire (Fedwire)' },
      { at: '2026-07-27T08:22:00Z', actor: 'a.clayborne@krotrust.org', action: 'Submitted for approval', detail: 'Amount above $10,000.00 threshold — routed to approver' },
      { at: '2026-07-27T10:02:00Z', actor: 'isadmin@krotrust.org', action: 'Approved', detail: 'Custody fee schedule verified against service agreement' },
    ],
  },
];

const EMPTY_FORM = {
  payeeId: '',
  network: '',
  method: 'ACH',
  fundingAccount: 'MERCURY_OPS',
  amount: '',
  payDate: plusDays(2),
  invoiceRef: '',
  category: '',
  memo: '',
  internalNote: '',
};

const WIZARD_STEPS = [
  { n: 1, label: 'Payee & Method',   icon: 'bi-person-badge' },
  { n: 2, label: 'Amount & Date',    icon: 'bi-cash-stack' },
  { n: 3, label: 'Coding & Notes',   icon: 'bi-tags' },
  { n: 4, label: 'Review & Submit',  icon: 'bi-clipboard-check' },
];

/* ─────────────────────────── Component ─────────────────────────── */

const PaymentWorkflow = () => {
  const [activeTab, setActiveTab] = useState('intake');
  const [payments, setPayments] = useState(SEED_PAYMENTS);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(EMPTY_FORM);
  const [flash, setFlash] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [networkFilter, setNetworkFilter] = useState('ALL');
  const [expandedRef, setExpandedRef] = useState(null);
  const [noteDraft, setNoteDraft] = useState({});

  useEffect(() => { document.title = 'Payment Workflow — KRO Exchange'; }, []);

  // Rehydrate from session so an in-progress queue survives navigation.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) setPayments(parsed);
      }
    } catch (_) { /* fall back to seed records */ }
  }, []);

  useEffect(() => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payments)); } catch (_) { /* quota — non-fatal */ }
  }, [payments]);

  useEffect(() => {
    if (!flash) return;
    const t = setTimeout(() => setFlash(null), 6000);
    return () => clearTimeout(t);
  }, [flash]);

  /* ── Form plumbing ─────────────────────────────────────────────── */

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const onPayeeChange = (payeeId) => {
    const p = PAYEES.find(x => x.id === payeeId);
    setForm(prev => ({
      ...prev,
      payeeId,
      network: p?.network || '',
      method: p?.method || prev.method,
    }));
  };

  const selectedPayee = PAYEES.find(p => p.id === form.payeeId);
  const selectedMethod = PAYMENT_METHODS.find(m => m.id === form.method);
  const selectedAccount = FUNDING_ACCOUNTS.find(a => a.id === form.fundingAccount);
  const amountNum = Number(form.amount) || 0;
  const needsApproval = amountNum >= APPROVAL_THRESHOLD;
  const overBalance = selectedAccount ? amountNum > selectedAccount.balance : false;

  const stepErrors = useMemo(() => {
    const e = {};
    if (step >= 1) {
      if (!form.payeeId) e.payeeId = 'Select a payee.';
      if (!form.method) e.method = 'Select a payment method.';
      if (!form.fundingAccount) e.fundingAccount = 'Select a funding account.';
    }
    if (step >= 2) {
      if (!form.amount || amountNum <= 0) e.amount = 'Enter an amount greater than zero.';
      if (overBalance) e.amount = 'Amount exceeds available balance in the selected funding account.';
      if (!form.payDate) e.payDate = 'Choose a payment date.';
      else if (form.payDate < today()) e.payDate = 'Payment date cannot be in the past.';
    }
    if (step >= 3) {
      if (!form.category) e.category = 'Select a GL category so the payment codes correctly.';
      if (!form.invoiceRef.trim()) e.invoiceRef = 'Enter the invoice or bill reference.';
    }
    return e;
  }, [step, form, amountNum, overBalance]);

  const stepValid = (n) => {
    if (n === 1) return Boolean(form.payeeId && form.method && form.fundingAccount);
    if (n === 2) return Boolean(form.amount && amountNum > 0 && !overBalance && form.payDate && form.payDate >= today());
    if (n === 3) return Boolean(form.category && form.invoiceRef.trim());
    return true;
  };

  const canContinue = stepValid(step);

  const resetWizard = () => { setForm(EMPTY_FORM); setStep(1); };

  const submitPayment = () => {
    if (!stepValid(1) || !stepValid(2) || !stepValid(3)) return;
    const ref = newRef();
    const ts = nowIso();
    const status = amountNum >= APPROVAL_THRESHOLD ? 'PENDING_APPROVAL' : 'SCHEDULED';
    const history = [
      { at: ts, actor: 'a.clayborne@krotrust.org', action: 'Payment created', detail: `Draft opened for ${selectedPayee?.name} — ${money(amountNum)} via ${methodLabel(form.method)}` },
      status === 'PENDING_APPROVAL'
        ? { at: ts, actor: 'a.clayborne@krotrust.org', action: 'Submitted for approval', detail: `Amount at or above ${money(APPROVAL_THRESHOLD)} threshold — routed to approver` }
        : { at: ts, actor: 'system', action: 'Scheduled', detail: `Below ${money(APPROVAL_THRESHOLD)} threshold — no approval required` },
    ];
    const record = {
      ref,
      payeeId: form.payeeId,
      payeeName: selectedPayee?.name || '—',
      network: form.network,
      amount: amountNum,
      method: form.method,
      fundingAccount: form.fundingAccount,
      payDate: form.payDate,
      invoiceRef: form.invoiceRef.trim(),
      category: form.category,
      memo: form.memo.trim(),
      internalNote: form.internalNote.trim(),
      status,
      createdAt: ts,
      createdBy: 'a.clayborne@krotrust.org',
      reconciled: false,
      history,
    };
    setPayments(prev => [record, ...prev]);
    setFlash({
      type: 'success',
      text: status === 'PENDING_APPROVAL'
        ? `${ref} submitted for approval — ${money(amountNum)} to ${record.payeeName}.`
        : `${ref} scheduled for ${fmtDate(form.payDate)} — ${money(amountNum)} to ${record.payeeName}.`,
    });
    resetWizard();
    setActiveTab('status');
  };

  /* ── Lifecycle actions ──────────────────────────────────────────── */

  const appendHistory = (ref, entry, patch = {}) => {
    setPayments(prev => prev.map(p =>
      p.ref === ref ? { ...p, ...patch, history: [...p.history, entry] } : p
    ));
  };

  const approve = (p) => {
    appendHistory(p.ref,
      { at: nowIso(), actor: 'isadmin@krotrust.org', action: 'Approved', detail: `Invoice ${p.invoiceRef} verified — released to the settlement queue` },
      { status: 'SCHEDULED', approvedBy: 'isadmin@krotrust.org', approvedAt: nowIso() }
    );
    setFlash({ type: 'success', text: `${p.ref} approved and scheduled for ${fmtDate(p.payDate)}.` });
  };

  const reject = (p) => {
    appendHistory(p.ref,
      { at: nowIso(), actor: 'isadmin@krotrust.org', action: 'Rejected', detail: 'Returned to originator — see operational notes for required changes' },
      { status: 'REJECTED' }
    );
    setFlash({ type: 'warning', text: `${p.ref} rejected and returned to the originator.` });
  };

  const raiseException = (p, code) => {
    const meta = EXCEPTION_CODES.find(c => c.code === code);
    appendHistory(p.ref,
      { at: nowIso(), actor: 'system', action: 'Exception raised', detail: `${code} — ${meta?.label || 'exception'}` },
      { status: 'EXCEPTION', exceptionCode: code }
    );
    setFlash({ type: 'danger', text: `${p.ref} moved to exception handling — ${code}.` });
  };

  const resolveException = (p) => {
    appendHistory(p.ref,
      { at: nowIso(), actor: 'a.clayborne@krotrust.org', action: 'Exception resolved', detail: `${p.exceptionCode} cleared — payment returned to the approval queue` },
      { status: 'PENDING_APPROVAL', exceptionCode: null }
    );
    setFlash({ type: 'success', text: `${p.ref} exception cleared — back in the approval queue.` });
  };

  const markSettled = (p) => {
    const bankRef = p.method === 'WIRE'
      ? `FEDWIRE-IMAD-${String(Date.now()).slice(-9)}`
      : `ACH-TRACE-${String(Date.now()).slice(-12)}`;
    appendHistory(p.ref,
      { at: nowIso(), actor: 'system', action: 'Settled', detail: `${bankRef} confirmed by ${p.method === 'WIRE' ? 'Column N.A.' : 'Mercury'}` },
      { status: 'SETTLED', settledAt: nowIso(), bankRef }
    );
    setFlash({ type: 'success', text: `${p.ref} settled — ${bankRef}.` });
  };

  const toggleReconciled = (p) => {
    const next = !p.reconciled;
    appendHistory(p.ref,
      { at: nowIso(), actor: 'a.clayborne@krotrust.org', action: next ? 'Reconciled' : 'Reconciliation reopened', detail: next ? 'Matched to bank feed — zero variance' : 'Match removed — pending re-review' },
      { reconciled: next, reconciledAt: next ? nowIso() : null }
    );
  };

  const saveNote = (p) => {
    const text = (noteDraft[p.ref] || '').trim();
    if (!text) return;
    appendHistory(p.ref,
      { at: nowIso(), actor: 'a.clayborne@krotrust.org', action: 'Operational note added', detail: text },
      { internalNote: text }
    );
    setNoteDraft(prev => ({ ...prev, [p.ref]: '' }));
    setFlash({ type: 'success', text: `Note saved to ${p.ref}.` });
  };

  /* ── Derived views ──────────────────────────────────────────────── */

  const kpis = useMemo(() => {
    const inFlight = payments.filter(p => ['PENDING_APPROVAL', 'APPROVED', 'SCHEDULED', 'PROCESSING'].includes(p.status));
    const awaiting = payments.filter(p => p.status === 'PENDING_APPROVAL');
    const exceptions = payments.filter(p => p.status === 'EXCEPTION');
    const settled = payments.filter(p => p.status === 'SETTLED');
    const unreconciled = settled.filter(p => !p.reconciled);
    return {
      inFlightCount: inFlight.length,
      inFlightValue: inFlight.reduce((s, p) => s + p.amount, 0),
      awaitingCount: awaiting.length,
      awaitingValue: awaiting.reduce((s, p) => s + p.amount, 0),
      exceptionCount: exceptions.length,
      settledCount: settled.length,
      settledValue: settled.reduce((s, p) => s + p.amount, 0),
      unreconciledCount: unreconciled.length,
    };
  }, [payments]);

  const filtered = useMemo(() => payments.filter(p =>
    (statusFilter === 'ALL' || p.status === statusFilter) &&
    (networkFilter === 'ALL' || p.network === networkFilter)
  ), [payments, statusFilter, networkFilter]);

  const approvalQueue = payments.filter(p => p.status === 'PENDING_APPROVAL');
  const exceptionQueue = payments.filter(p => p.status === 'EXCEPTION');
  const settledPayments = payments.filter(p => p.status === 'SETTLED');

  const auditEntries = useMemo(() => payments
    .flatMap(p => p.history.map(h => ({ ...h, ref: p.ref, payeeName: p.payeeName })))
    .sort((a, b) => new Date(b.at) - new Date(a.at)),
  [payments]);

  /* ── Small presentational helpers ───────────────────────────────── */

  const StatusBadge = ({ status }) => {
    const m = STATUS_META[status] || STATUS_META.DRAFT;
    return (
      <span className={`badge bg-${m.badge}`} style={{ fontSize: '0.7rem' }}>
        <i className={`bi ${m.icon} me-1`}></i>{m.label}
      </span>
    );
  };

  const PipelineBar = ({ status }) => {
    if (status === 'EXCEPTION' || status === 'REJECTED') {
      return <div className="progress" style={{ height: 4 }}><div className="progress-bar bg-danger" style={{ width: '100%' }} /></div>;
    }
    const idx = PIPELINE.indexOf(status);
    const pct = idx < 0 ? 0 : ((idx + 1) / PIPELINE.length) * 100;
    return (
      <div className="progress" style={{ height: 4 }}>
        <div className={`progress-bar ${status === 'SETTLED' ? 'bg-success' : 'bg-primary'}`} style={{ width: `${pct}%` }} />
      </div>
    );
  };

  const KpiCard = ({ icon, label, value, sub, tone }) => (
    <div className="col-6 col-lg-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <div className="d-flex align-items-center gap-2 mb-1">
            <i className={`bi ${icon} text-${tone}`}></i>
            <span className="text-muted text-uppercase" style={{ fontSize: '0.68rem', letterSpacing: '0.6px', fontWeight: 700 }}>{label}</span>
          </div>
          <div className="fw-bold" style={{ fontSize: '1.5rem' }}>{value}</div>
          <small className="text-muted">{sub}</small>
        </div>
      </div>
    </div>
  );

  const Field = ({ label, required, error, hint, children }) => (
    <div className="mb-3">
      <label className="form-label fw-semibold" style={{ fontSize: '0.83rem' }}>
        {label}{required && <span className="text-danger ms-1">*</span>}
      </label>
      {children}
      {hint && !error && <div className="form-text" style={{ fontSize: '0.74rem' }}>{hint}</div>}
      {error && <div className="text-danger" style={{ fontSize: '0.74rem' }}><i className="bi bi-exclamation-circle me-1"></i>{error}</div>}
    </div>
  );

  const ReviewRow = ({ label, value, strong }) => (
    <div className="d-flex justify-content-between align-items-start py-2 border-bottom">
      <span className="text-muted" style={{ fontSize: '0.8rem' }}>{label}</span>
      <span className={strong ? 'fw-bold text-end' : 'text-end'} style={{ fontSize: '0.85rem', maxWidth: '62%' }}>{value || '—'}</span>
    </div>
  );

  /* ─────────────────────────── Render ─────────────────────────── */

  return (
    <div className="container-fluid py-4">

      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb mb-0">
          <li className="breadcrumb-item"><Link to="/kro-exchange/settlement-hub">KRO Exchange</Link></li>
          <li className="breadcrumb-item active" aria-current="page">Payment Workflow</li>
        </ol>
      </nav>

      {/* Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
        <div>
          <h1 className="display-6 fw-bold mb-1">
            <i className="bi bi-credit-card-2-front me-2 text-primary"></i>Payment Workflow
          </h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.88rem' }}>
            Payment intake, approval routing, exception handling, and reconciliation across KRO Exchange networks.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-light text-dark border" style={{ fontSize: '0.72rem' }}>
            <i className="bi bi-shield-check text-success me-1"></i>
            Approval threshold {money(APPROVAL_THRESHOLD)}
          </span>
          <button className="btn btn-primary" onClick={() => { setActiveTab('intake'); resetWizard(); }}>
            <i className="bi bi-plus-lg me-1"></i>New Payment
          </button>
        </div>
      </div>

      {/* Flash */}
      {flash && (
        <div className={`alert alert-${flash.type} alert-dismissible py-2 mb-3`} style={{ fontSize: '0.85rem' }} role="status">
          <i className="bi bi-info-circle me-1"></i>{flash.text}
          <button type="button" className="btn-close btn-sm" onClick={() => setFlash(null)} aria-label="Dismiss"></button>
        </div>
      )}

      {/* KPI strip */}
      <div className="row g-3 mb-4">
        <KpiCard icon="bi-send-fill"                 label="In Flight"      tone="primary" value={kpis.inFlightCount}   sub={`${shortMoney(kpis.inFlightValue)} pending settlement`} />
        <KpiCard icon="bi-hourglass-split"           label="Awaiting Approval" tone="warning" value={kpis.awaitingCount} sub={`${shortMoney(kpis.awaitingValue)} needs a decision`} />
        <KpiCard icon="bi-exclamation-triangle-fill" label="Exceptions"     tone="danger"  value={kpis.exceptionCount}  sub={kpis.exceptionCount ? 'Action required' : 'None open'} />
        <KpiCard icon="bi-patch-check-fill"          label="Settled"        tone="success" value={kpis.settledCount}    sub={`${kpis.unreconciledCount} awaiting reconciliation`} />
      </div>

      {/* Tabs */}
      <ul className="nav nav-pills mb-4" style={{ gap: '0.25rem' }}>
        {[
          { id: 'intake',   icon: 'bi-pencil-square',              label: 'Payment Intake' },
          { id: 'status',   icon: 'bi-list-check',                 label: 'Status Tracking', count: payments.length },
          { id: 'approvals',icon: 'bi-check2-square',              label: 'Approvals & Exceptions', count: approvalQueue.length + exceptionQueue.length },
          { id: 'audit',    icon: 'bi-clock-history',              label: 'Audit Trail' },
          { id: 'recon',    icon: 'bi-arrow-left-right',           label: 'Reconciliation', count: kpis.unreconciledCount },
        ].map(t => (
          <li key={t.id} className="nav-item">
            <button className={`nav-link ${activeTab === t.id ? 'active' : ''}`} onClick={() => setActiveTab(t.id)}>
              <i className={`bi ${t.icon} me-1`}></i>{t.label}
              {t.count > 0 && <span className="badge bg-white text-dark ms-2" style={{ fontSize: '0.62rem' }}>{t.count}</span>}
            </button>
          </li>
        ))}
      </ul>

      {/* ══════════════════ TAB: PAYMENT INTAKE ══════════════════ */}
      {activeTab === 'intake' && (
        <div className="row g-4">
          <div className="col-12 col-xl-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                {/* Stepper */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  {WIZARD_STEPS.map((s, i) => {
                    const done = step > s.n;
                    const current = step === s.n;
                    return (
                      <React.Fragment key={s.n}>
                        <button
                          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2"
                          onClick={() => { if (s.n < step || stepValid(step)) setStep(s.n); }}
                          disabled={s.n > step && !stepValid(step)}
                          style={{ cursor: s.n > step && !stepValid(step) ? 'not-allowed' : 'pointer' }}
                        >
                          <span
                            className="rounded-circle d-inline-grid place-items-center fw-bold"
                            style={{
                              width: 30, height: 30, display: 'grid', placeItems: 'center', fontSize: '0.8rem',
                              background: done ? '#198754' : current ? '#0d6efd' : '#e9ecef',
                              color: done || current ? '#fff' : '#6c757d',
                            }}
                          >
                            {done ? <i className="bi bi-check-lg"></i> : s.n}
                          </span>
                          <span className={current ? 'fw-bold text-dark' : 'text-muted'} style={{ fontSize: '0.8rem' }}>{s.label}</span>
                        </button>
                        {i < WIZARD_STEPS.length - 1 && <div className="flex-grow-1 d-none d-md-block" style={{ height: 2, background: step > s.n ? '#198754' : '#e9ecef', minWidth: 20 }} />}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>

              <div className="card-body p-4">
                {/* STEP 1 — Payee & method */}
                {step === 1 && (
                  <>
                    <h6 className="fw-bold mb-3">Who are you paying?</h6>
                    <Field label="Payee" required error={stepErrors.payeeId}>
                      <select className={`form-select ${stepErrors.payeeId ? 'is-invalid' : ''}`} value={form.payeeId} onChange={e => onPayeeChange(e.target.value)}>
                        <option value="">Select a payee…</option>
                        {EXCHANGE_NETWORKS.map(net => (
                          <optgroup key={net} label={net}>
                            {PAYEES.filter(p => p.network === net).map(p => (
                              <option key={p.id} value={p.id}>{p.name} · {p.terms}</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    </Field>

                    {selectedPayee && (
                      <div className="alert alert-light border py-2 mb-3" style={{ fontSize: '0.8rem' }}>
                        <div className="d-flex flex-wrap gap-3">
                          <span><i className="bi bi-diagram-3 me-1 text-primary"></i><strong>Network:</strong> {selectedPayee.network}</span>
                          <span><i className="bi bi-calendar3 me-1 text-primary"></i><strong>Terms:</strong> {selectedPayee.terms}</span>
                          <span>
                            <i className={`bi ${selectedPayee.verified ? 'bi-patch-check-fill text-success' : 'bi-exclamation-triangle-fill text-warning'} me-1`}></i>
                            <strong>Payee record:</strong> {selectedPayee.verified ? 'Verified' : 'Verification pending'}
                          </span>
                        </div>
                      </div>
                    )}

                    <Field label="Payment method" required hint="Method defaults to the payee's record and can be changed per payment.">
                      <div className="row g-2">
                        {PAYMENT_METHODS.map(m => (
                          <div key={m.id} className="col-12 col-sm-6">
                            <button
                              type="button"
                              onClick={() => setField('method', m.id)}
                              className={`btn w-100 text-start p-3 ${form.method === m.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                            >
                              <div className="fw-semibold" style={{ fontSize: '0.85rem' }}><i className={`bi ${m.icon} me-2`}></i>{m.label}</div>
                              <div style={{ fontSize: '0.72rem', opacity: 0.85 }}>{m.speed} · {m.fee}</div>
                            </button>
                          </div>
                        ))}
                      </div>
                    </Field>

                    <Field label="Pay from" required error={stepErrors.fundingAccount}>
                      <select className="form-select" value={form.fundingAccount} onChange={e => setField('fundingAccount', e.target.value)}>
                        {FUNDING_ACCOUNTS.map(a => (
                          <option key={a.id} value={a.id}>{a.label} {a.masked} — {money(a.balance)} available</option>
                        ))}
                      </select>
                    </Field>
                  </>
                )}

                {/* STEP 2 — Amount & schedule */}
                {step === 2 && (
                  <>
                    <h6 className="fw-bold mb-3">How much, and when?</h6>
                    <Field label="Amount (USD)" required error={stepErrors.amount}>
                      <div className="input-group">
                        <span className="input-group-text">$</span>
                        <input
                          type="number" min="0" step="0.01" inputMode="decimal"
                          className={`form-control form-control-lg ${stepErrors.amount ? 'is-invalid' : ''}`}
                          value={form.amount}
                          onChange={e => setField('amount', e.target.value)}
                          placeholder="0.00"
                        />
                      </div>
                    </Field>

                    {amountNum > 0 && (
                      <div className={`alert py-2 mb-3 ${needsApproval ? 'alert-warning' : 'alert-success'}`} style={{ fontSize: '0.8rem' }}>
                        <i className={`bi ${needsApproval ? 'bi-person-check' : 'bi-lightning-charge'} me-1`}></i>
                        {needsApproval
                          ? <>At {money(amountNum)} this payment is at or above the {money(APPROVAL_THRESHOLD)} threshold and will route to an approver before scheduling.</>
                          : <>At {money(amountNum)} this payment is below the {money(APPROVAL_THRESHOLD)} threshold and schedules directly.</>}
                      </div>
                    )}

                    <Field label="Payment date" required error={stepErrors.payDate} hint={selectedMethod ? `${selectedMethod.label} typically delivers in ${selectedMethod.speed.toLowerCase()}.` : undefined}>
                      <input type="date" className={`form-control ${stepErrors.payDate ? 'is-invalid' : ''}`} min={today()} value={form.payDate} onChange={e => setField('payDate', e.target.value)} />
                    </Field>

                    <div className="card bg-light border-0">
                      <div className="card-body py-3">
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.82rem' }}>
                          <span className="text-muted">Funding account</span>
                          <span className="fw-semibold">{accountLabel(form.fundingAccount)}</span>
                        </div>
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.82rem' }}>
                          <span className="text-muted">Available balance</span>
                          <span className="fw-semibold">{money(selectedAccount?.balance)}</span>
                        </div>
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.82rem' }}>
                          <span className="text-muted">Balance after payment</span>
                          <span className={`fw-semibold ${overBalance ? 'text-danger' : ''}`}>{money((selectedAccount?.balance || 0) - amountNum)}</span>
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* STEP 3 — Coding & notes */}
                {step === 3 && (
                  <>
                    <h6 className="fw-bold mb-3">Code the payment and add context</h6>
                    <Field label="Invoice / bill reference" required error={stepErrors.invoiceRef} hint="Used to match the payment during reconciliation.">
                      <input type="text" className={`form-control ${stepErrors.invoiceRef ? 'is-invalid' : ''}`} value={form.invoiceRef} onChange={e => setField('invoiceRef', e.target.value)} placeholder="INV-0000-0000" />
                    </Field>

                    <Field label="Category / GL account" required error={stepErrors.category}>
                      <select className={`form-select ${stepErrors.category ? 'is-invalid' : ''}`} value={form.category} onChange={e => setField('category', e.target.value)}>
                        <option value="">Select a category…</option>
                        {GL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </Field>

                    <Field label="Memo to payee" hint="Appears on the remittance advice the payee receives.">
                      <input type="text" className="form-control" value={form.memo} onChange={e => setField('memo', e.target.value)} maxLength={140} placeholder="What is this payment for?" />
                    </Field>

                    <Field label="Operational note (internal)" hint="Internal only — never sent to the payee. Recorded in the audit trail.">
                      <textarea className="form-control" rows={3} value={form.internalNote} onChange={e => setField('internalNote', e.target.value)} maxLength={500} placeholder="Approval context, contract reference, exception history…" />
                    </Field>
                  </>
                )}

                {/* STEP 4 — Review */}
                {step === 4 && (
                  <>
                    <h6 className="fw-bold mb-3">Review before submitting</h6>
                    <div className="mb-3">
                      <ReviewRow label="Payee"            value={selectedPayee?.name} strong />
                      <ReviewRow label="Exchange network" value={form.network} />
                      <ReviewRow label="Amount"           value={money(amountNum)} strong />
                      <ReviewRow label="Method"           value={`${methodLabel(form.method)} · ${selectedMethod?.speed}`} />
                      <ReviewRow label="Pay from"         value={accountLabel(form.fundingAccount)} />
                      <ReviewRow label="Payment date"     value={fmtDate(form.payDate)} />
                      <ReviewRow label="Invoice reference"value={form.invoiceRef} />
                      <ReviewRow label="Category"         value={form.category} />
                      <ReviewRow label="Memo to payee"    value={form.memo} />
                      <ReviewRow label="Operational note" value={form.internalNote} />
                      <ReviewRow label="Routing"          value={needsApproval ? 'Requires approval before scheduling' : 'Schedules directly — below approval threshold'} />
                    </div>
                    <div className="alert alert-light border py-2" style={{ fontSize: '0.78rem' }}>
                      <i className="bi bi-info-circle me-1"></i>
                      Submitting records the payment with a full audit entry. Nothing leaves the funding account until the payment reaches its scheduled date and, where required, has been approved.
                    </div>
                  </>
                )}
              </div>

              {/* Wizard footer */}
              <div className="card-footer bg-white border-top d-flex justify-content-between align-items-center py-3">
                <button className="btn btn-outline-secondary" onClick={() => (step > 1 ? setStep(step - 1) : resetWizard())}>
                  <i className="bi bi-arrow-left me-1"></i>{step > 1 ? 'Back' : 'Clear'}
                </button>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted">Step {step} of {WIZARD_STEPS.length}</small>
                  {step < WIZARD_STEPS.length ? (
                    <button className="btn btn-primary" disabled={!canContinue} onClick={() => setStep(step + 1)}>
                      Continue<i className="bi bi-arrow-right ms-1"></i>
                    </button>
                  ) : (
                    <button className="btn btn-success fw-bold" onClick={submitPayment}>
                      <i className="bi bi-check2-circle me-1"></i>Submit Payment
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Side rail */}
          <div className="col-12 col-xl-4">
            <div className="card border-0 shadow-sm mb-3">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-signpost-2 me-2 text-primary"></i>How intake works</h6>
              </div>
              <div className="card-body">
                {[
                  { t: 'Payee & method',  d: 'Payee records carry the default method, terms, and network so intake starts pre-filled.' },
                  { t: 'Amount & date',   d: `Payments at or above ${money(APPROVAL_THRESHOLD)} route to an approver. Below that, they schedule directly.` },
                  { t: 'Coding & notes',  d: 'Invoice reference and GL category are required so every payment reconciles cleanly.' },
                  { t: 'Review & submit', d: 'A full audit entry is written on submission and on every state change afterwards.' },
                ].map((s, i) => (
                  <div key={s.t} className="d-flex gap-3 mb-3">
                    <span className="badge bg-primary rounded-circle flex-shrink-0" style={{ width: 24, height: 24, display: 'grid', placeItems: 'center' }}>{i + 1}</span>
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '0.83rem' }}>{s.t}</div>
                      <div className="text-muted" style={{ fontSize: '0.78rem' }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3">
                <h6 className="mb-0 fw-bold"><i className="bi bi-wallet2 me-2 text-primary"></i>Funding accounts</h6>
              </div>
              <div className="card-body">
                {FUNDING_ACCOUNTS.map(a => (
                  <div key={a.id} className="d-flex justify-content-between align-items-center py-2 border-bottom">
                    <div>
                      <div className="fw-semibold" style={{ fontSize: '0.82rem' }}>{a.label}</div>
                      <small className="text-muted">{a.masked}</small>
                    </div>
                    <span className="fw-bold" style={{ fontSize: '0.85rem' }}>{shortMoney(a.balance)}</span>
                  </div>
                ))}
                <small className="text-muted d-block mt-2" style={{ fontSize: '0.72rem' }}>
                  Balances are read-only in Studio. Movement of funds is initiated by the banking partner, not by this interface.
                </small>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: STATUS TRACKING ══════════════════ */}
      {activeTab === 'status' && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h6 className="mb-0 fw-bold"><i className="bi bi-list-check me-2 text-primary"></i>Payment Status</h6>
              <div className="d-flex gap-2">
                <select className="form-select form-select-sm" style={{ width: 190 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
                  <option value="ALL">All statuses</option>
                  {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <select className="form-select form-select-sm" style={{ width: 200 }} value={networkFilter} onChange={e => setNetworkFilter(e.target.value)}>
                  <option value="ALL">All networks</option>
                  {EXCHANGE_NETWORKS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {filtered.length === 0 ? (
              <div className="text-center text-muted py-5">
                <i className="bi bi-inbox d-block mb-2" style={{ fontSize: '1.8rem' }}></i>
                No payments match the current filters.
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0">
                  <thead className="table-light">
                    <tr style={{ fontSize: '0.76rem' }}>
                      <th className="ps-3">Reference</th>
                      <th>Payee</th>
                      <th className="text-end">Amount</th>
                      <th>Method</th>
                      <th>Pay date</th>
                      <th>Status</th>
                      <th className="text-end pe-3">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(p => (
                      <React.Fragment key={p.ref}>
                        <tr style={{ fontSize: '0.82rem' }}>
                          <td className="ps-3">
                            <code style={{ fontSize: '0.72rem' }}>{p.ref}</code>
                            <PipelineBar status={p.status} />
                          </td>
                          <td>
                            <div className="fw-semibold">{p.payeeName}</div>
                            <small className="text-muted">{p.network}</small>
                          </td>
                          <td className="text-end fw-bold">{money(p.amount)}</td>
                          <td>{methodLabel(p.method)}</td>
                          <td>{fmtDate(p.payDate)}</td>
                          <td>
                            <StatusBadge status={p.status} />
                            {p.exceptionCode && <div><small className="text-danger">{p.exceptionCode}</small></div>}
                          </td>
                          <td className="text-end pe-3">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => setExpandedRef(expandedRef === p.ref ? null : p.ref)}>
                              <i className={`bi ${expandedRef === p.ref ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                            </button>
                          </td>
                        </tr>
                        {expandedRef === p.ref && (
                          <tr className="bg-light">
                            <td colSpan={7} className="px-3 py-3">
                              <div className="row g-3">
                                <div className="col-12 col-lg-5">
                                  <div className="fw-bold mb-2" style={{ fontSize: '0.78rem' }}>PAYMENT DETAIL</div>
                                  <ReviewRow label="Invoice reference" value={p.invoiceRef} />
                                  <ReviewRow label="Category"         value={p.category} />
                                  <ReviewRow label="Pay from"         value={accountLabel(p.fundingAccount)} />
                                  <ReviewRow label="Memo to payee"    value={p.memo} />
                                  <ReviewRow label="Created"          value={`${fmtDateTime(p.createdAt)} · ${p.createdBy}`} />
                                  {p.approvedBy && <ReviewRow label="Approved" value={`${fmtDateTime(p.approvedAt)} · ${p.approvedBy}`} />}
                                  {p.bankRef && <ReviewRow label="Bank reference" value={p.bankRef} />}
                                </div>
                                <div className="col-12 col-lg-4">
                                  <div className="fw-bold mb-2" style={{ fontSize: '0.78rem' }}>HISTORY</div>
                                  <ul className="list-unstyled mb-0" style={{ fontSize: '0.76rem' }}>
                                    {p.history.slice().reverse().map((h, i) => (
                                      <li key={i} className="d-flex gap-2 mb-2">
                                        <i className="bi bi-dot text-primary"></i>
                                        <div>
                                          <div className="fw-semibold">{h.action}</div>
                                          <div className="text-muted">{h.detail}</div>
                                          <small className="text-muted">{fmtDateTime(h.at)} · {h.actor}</small>
                                        </div>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="col-12 col-lg-3">
                                  <div className="fw-bold mb-2" style={{ fontSize: '0.78rem' }}>OPERATIONAL NOTE</div>
                                  {p.internalNote && <div className="alert alert-light border py-2" style={{ fontSize: '0.76rem' }}>{p.internalNote}</div>}
                                  <textarea
                                    className="form-control form-control-sm mb-2" rows={3}
                                    placeholder="Add an internal note…"
                                    value={noteDraft[p.ref] || ''}
                                    onChange={e => setNoteDraft(prev => ({ ...prev, [p.ref]: e.target.value }))}
                                  />
                                  <div className="d-flex flex-wrap gap-2">
                                    <button className="btn btn-sm btn-outline-primary" onClick={() => saveNote(p)} disabled={!(noteDraft[p.ref] || '').trim()}>
                                      <i className="bi bi-save me-1"></i>Save note
                                    </button>
                                    {['SCHEDULED', 'APPROVED'].includes(p.status) && (
                                      <button className="btn btn-sm btn-outline-success" onClick={() => markSettled(p)}>
                                        <i className="bi bi-patch-check me-1"></i>Mark settled
                                      </button>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            Pipeline: Draft → Pending Approval → Approved → Scheduled → Processing → Settled. Exceptions and rejections branch off the pipeline and return to it once cleared.
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: APPROVALS & EXCEPTIONS ══════════════════ */}
      {activeTab === 'approvals' && (
        <div className="row g-4">
          <div className="col-12 col-xl-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold"><i className="bi bi-check2-square me-2 text-warning"></i>Approval Queue</h6>
                <span className="badge bg-warning">{approvalQueue.length} awaiting</span>
              </div>
              <div className="card-body">
                {approvalQueue.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-check2-all d-block mb-2" style={{ fontSize: '1.6rem' }}></i>
                    Nothing awaiting approval.
                  </div>
                ) : approvalQueue.map(p => (
                  <div key={p.ref} className="border rounded-3 p-3 mb-3">
                    <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                      <div>
                        <div className="fw-bold">{p.payeeName}</div>
                        <small className="text-muted"><code style={{ fontSize: '0.7rem' }}>{p.ref}</code> · {p.invoiceRef} · {p.network}</small>
                      </div>
                      <div className="text-end">
                        <div className="fw-bold" style={{ fontSize: '1.05rem' }}>{money(p.amount)}</div>
                        <small className="text-muted">{methodLabel(p.method)} · {fmtDate(p.payDate)}</small>
                      </div>
                    </div>
                    <div className="text-muted mb-2" style={{ fontSize: '0.78rem' }}>
                      <i className="bi bi-tag me-1"></i>{p.category}
                      {p.memo && <> · <i className="bi bi-chat-left-text me-1"></i>{p.memo}</>}
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      <button className="btn btn-sm btn-success" onClick={() => approve(p)}>
                        <i className="bi bi-check-lg me-1"></i>Approve
                      </button>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => reject(p)}>
                        <i className="bi bi-x-lg me-1"></i>Reject
                      </button>
                      <div className="dropdown">
                        <button className="btn btn-sm btn-outline-warning dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
                          <i className="bi bi-flag me-1"></i>Raise exception
                        </button>
                        <ul className="dropdown-menu">
                          {EXCEPTION_CODES.map(c => (
                            <li key={c.code}>
                              <button className="dropdown-item" style={{ fontSize: '0.8rem' }} onClick={() => raiseException(p, c.code)}>
                                <span className="fw-semibold d-block">{c.code}</span>
                                <small className="text-muted">{c.label}</small>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold"><i className="bi bi-exclamation-triangle-fill me-2 text-danger"></i>Exception Handling</h6>
                <span className={`badge bg-${exceptionQueue.length ? 'danger' : 'success'}`}>{exceptionQueue.length} open</span>
              </div>
              <div className="card-body">
                {exceptionQueue.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-shield-check d-block mb-2 text-success" style={{ fontSize: '1.6rem' }}></i>
                    No open exceptions.
                  </div>
                ) : exceptionQueue.map(p => {
                  const meta = EXCEPTION_CODES.find(c => c.code === p.exceptionCode);
                  return (
                    <div key={p.ref} className="border border-danger border-opacity-25 rounded-3 p-3 mb-3" style={{ background: 'rgba(220,53,69,0.03)' }}>
                      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-2">
                        <div>
                          <div className="fw-bold">{p.payeeName}</div>
                          <small className="text-muted"><code style={{ fontSize: '0.7rem' }}>{p.ref}</code> · {p.invoiceRef}</small>
                        </div>
                        <div className="fw-bold">{money(p.amount)}</div>
                      </div>
                      <div className="alert alert-danger py-2 mb-2" style={{ fontSize: '0.78rem' }}>
                        <strong>{p.exceptionCode}</strong> — {meta?.label || 'Exception raised.'}
                      </div>
                      {p.internalNote && (
                        <div className="text-muted mb-2" style={{ fontSize: '0.76rem' }}>
                          <i className="bi bi-sticky me-1"></i>{p.internalNote}
                        </div>
                      )}
                      <div className="d-flex flex-wrap gap-2">
                        <button className="btn btn-sm btn-primary" onClick={() => resolveException(p)}>
                          <i className="bi bi-arrow-counterclockwise me-1"></i>Resolve & requeue
                        </button>
                        <button className="btn btn-sm btn-outline-dark" onClick={() => reject(p)}>
                          <i className="bi bi-x-circle me-1"></i>Cancel payment
                        </button>
                      </div>
                    </div>
                  );
                })}

                <div className="mt-3">
                  <div className="fw-bold mb-2" style={{ fontSize: '0.78rem' }}>EXCEPTION REASON CODES</div>
                  <div className="table-responsive">
                    <table className="table table-sm mb-0" style={{ fontSize: '0.74rem' }}>
                      <tbody>
                        {EXCEPTION_CODES.map(c => (
                          <tr key={c.code}>
                            <td className="fw-semibold" style={{ whiteSpace: 'nowrap' }}>{c.code}</td>
                            <td className="text-muted">{c.label}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: AUDIT TRAIL ══════════════════ */}
      {activeTab === 'audit' && (
        <div className="card border-0 shadow-sm">
          <div className="card-header bg-white border-bottom py-3">
            <h6 className="mb-0 fw-bold"><i className="bi bi-clock-history me-2 text-primary"></i>Audit Trail &amp; History</h6>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="table-light">
                  <tr style={{ fontSize: '0.76rem' }}>
                    <th className="ps-3">Timestamp</th>
                    <th>Reference</th>
                    <th>Payee</th>
                    <th>Action</th>
                    <th>Detail</th>
                    <th className="pe-3">Actor</th>
                  </tr>
                </thead>
                <tbody>
                  {auditEntries.map((h, i) => (
                    <tr key={`${h.ref}-${i}`} style={{ fontSize: '0.8rem' }}>
                      <td className="ps-3" style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(h.at)}</td>
                      <td><code style={{ fontSize: '0.7rem' }}>{h.ref}</code></td>
                      <td>{h.payeeName}</td>
                      <td className="fw-semibold">{h.action}</td>
                      <td className="text-muted">{h.detail}</td>
                      <td className="pe-3">
                        {h.actor === 'system'
                          ? <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>system</span>
                          : <small>{h.actor}</small>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
            <i className="bi bi-info-circle me-1"></i>
            {auditEntries.length} entries across {payments.length} payments. Every state change writes an entry with actor and timestamp — entries are append-only and are never edited or removed.
          </div>
        </div>
      )}

      {/* ══════════════════ TAB: RECONCILIATION ══════════════════ */}
      {activeTab === 'recon' && (
        <>
          <div className="row g-3 mb-4">
            <KpiCard icon="bi-patch-check-fill"  label="Settled"        tone="success" value={settledPayments.length} sub={shortMoney(kpis.settledValue)} />
            <KpiCard icon="bi-check2-circle"     label="Matched"        tone="primary" value={settledPayments.filter(p => p.reconciled).length} sub="Confirmed against bank feed" />
            <KpiCard icon="bi-hourglass"         label="Unmatched"      tone="warning" value={kpis.unreconciledCount} sub="Awaiting reconciliation" />
            <KpiCard icon="bi-graph-up"          label="Match Rate"     tone="info"
              value={settledPayments.length ? `${Math.round((settledPayments.filter(p => p.reconciled).length / settledPayments.length) * 100)}%` : '—'}
              sub="Settled payments matched" />
          </div>

          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom py-3">
              <h6 className="mb-0 fw-bold"><i className="bi bi-arrow-left-right me-2 text-primary"></i>Reconciliation</h6>
            </div>
            <div className="card-body p-0">
              {settledPayments.length === 0 ? (
                <div className="text-center text-muted py-5">
                  <i className="bi bi-inbox d-block mb-2" style={{ fontSize: '1.8rem' }}></i>
                  No settled payments to reconcile yet.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr style={{ fontSize: '0.76rem' }}>
                        <th className="ps-3">Reference</th>
                        <th>Payee</th>
                        <th className="text-end">Amount</th>
                        <th>Bank reference</th>
                        <th>Settled</th>
                        <th>Variance</th>
                        <th className="pe-3">Match</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settledPayments.map(p => (
                        <tr key={p.ref} style={{ fontSize: '0.8rem' }}>
                          <td className="ps-3"><code style={{ fontSize: '0.7rem' }}>{p.ref}</code></td>
                          <td>
                            <div className="fw-semibold">{p.payeeName}</div>
                            <small className="text-muted">{p.invoiceRef} · {p.category}</small>
                          </td>
                          <td className="text-end fw-bold">{money(p.amount)}</td>
                          <td><code style={{ fontSize: '0.68rem' }}>{p.bankRef || '—'}</code></td>
                          <td style={{ whiteSpace: 'nowrap' }}>{fmtDateTime(p.settledAt)}</td>
                          <td><span className="badge bg-success bg-opacity-10 text-success" style={{ fontSize: '0.68rem' }}>{money(0)}</span></td>
                          <td className="pe-3">
                            <button
                              className={`btn btn-sm ${p.reconciled ? 'btn-success' : 'btn-outline-warning'}`}
                              onClick={() => toggleReconciled(p)}
                            >
                              <i className={`bi ${p.reconciled ? 'bi-check2-circle' : 'bi-hourglass'} me-1`}></i>
                              {p.reconciled ? 'Matched' : 'Match'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div className="card-footer bg-light text-muted" style={{ fontSize: '0.74rem' }}>
              <i className="bi bi-info-circle me-1"></i>
              Variance compares the instructed amount to the amount confirmed by the banking partner. A non-zero variance holds the match open until it is investigated and noted.
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PaymentWorkflow;
